import React, { useEffect, useState } from 'react';
import { Wrench, ChevronDown, ArrowLeft, Lock, Circle, Mail, Key, Loader2, Eye, EyeOff } from 'lucide-react';
import { Card } from './shadcn-ui/card';
import { Button } from './shadcn-ui/button';
import { Badge } from './shadcn-ui/badge';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { appAxios } from '@/network/base/appAxios';
import { toast } from 'sonner';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from './shadcn-ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './shadcn-ui/dialog';

const PermitProjectDetail = () => {
    const [permitData, setPermitData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showDocDialog, setShowDocDialog] = useState(false);
    const [portalCreds, setPortalCreds] = useState(null);
    const [credsLoading, setCredsLoading] = useState(false);
    const [credsError, setCredsError] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const { permitId, permitChatId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const steps = [
        { label: 'New', value: 'new' },
        { label: 'Base Fees Payment', value: 'payment' },
        { label: 'In Progress', value: 'in_progress' },
        { label: 'County Fees', value: 'city_fee' },
        { label: 'Submitted', value: 'submitted' },
        { label: 'Review', value: 'review' },
        { label: 'Approved', value: 'approved' }
    ];

    // Helper functions
    const getPermitTypeName = (typeCode) => {
        const typeMap = {
            '0': 'Bldg Alter Repair',
            '1': 'Electrical Vehicle Charger',
            '2': 'Bldg Alter Repair',
            '3': 'Fire Sprinkler',
            '4': 'HVAC',
            '5': 'Plumbing',
            '6': 'Pressure Vessel',
            '7': 'Temporary Special Event',
        };
        return typeMap[typeCode] || 'General';
    };

    const getStatusDisplay = (status, baseFeesPaid) => {
        if (!baseFeesPaid) {
            return 'Payment';
        }

        const statusMap = {
            draft: 'In Progress',
            payment: 'In Progress',
            city_fee: 'City Fees',
            submitted: 'Submitted',
            review: 'Review',
            approved: 'Approved',
            added_to_process_queue: 'In Progress',
            pending: 'In Progress',
            in_progress: 'In Progress'
        };
        return statusMap[status] || 'In Progress';
    };

    const getCurrentStep = (status, baseFeesPaid, countyFeesPaid, countyFeesPaymentLink) => {
        if (!baseFeesPaid) {
            return 1;
        }
        if (baseFeesPaid && !countyFeesPaid && countyFeesPaymentLink) {
            return 3;
        }
        if (countyFeesPaid && status !== 'submitted' && status !== 'review' && status !== 'approved') {
            return 3.5;
        }
        const stepMap = {
            draft: 2,
            payment: 2,
            added_to_process_queue: 2,
            pending: 2,
            in_progress: 2,
            automation_failed: 2,
            city_fee: 3,
            submitted: 4,
            review: 5,
            approved: 6
        };
        return stepMap[status] || 2;
    };

    const getProgress = (status, baseFeesPaid, countyFeesPaid, countyFeesPaymentLink) => {
        const currentStep = getCurrentStep(status, baseFeesPaid, countyFeesPaid, countyFeesPaymentLink);
        return Math.round((currentStep / (steps.length - 1)) * 100);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getPermitData = async () => {
        try {
            setLoading(true);
            const response = await appAxios.get(`/Permit/v1.0/permit-details`, {
                params: { permitId }
            });
            setPermitData(response.data.data);
        } catch (error) {
            console.error('Failed to fetch permit data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (permitId) {
            getPermitData();
        }
    }, [permitId]);

    useEffect(() => {
        if (permitData?.base_fees_paid && permitId && permitChatId) {
            localStorage.setItem(`permitChat_${permitChatId}_permitId`, permitId);
        }
    }, [permitData, permitId, permitChatId]);

    const handlePayment = async (permitId, userId, permitChatId) => {
        const response = await appAxios.post(`/Payment/v1.0/create-base-fees-payment-link`, {
            permitId: permitId,
            userId: userId,
            permitChatId: permitChatId
        });

        if (response.data.status == "Success") {
            window.location.href = response.data.data.url;
        } else {
            toast.error("Error in creating payment link . Please try again later.");
        }
    };

    const handleViewDocuments = async () => {
        setPortalCreds(null);
        setCredsError(false);
        setShowPassword(false);
        setShowDocDialog(true);
        setCredsLoading(true);
        try {
            const response = await appAxios.get(`/Permit/v1.0/get-portal-creds/${permitData.portal_name}`);
            setPortalCreds(response.data.data);
        } catch (error) {
            console.error('Failed to fetch portal creds:', error);
            setCredsError(true);
        } finally {
            setCredsLoading(false);
        }
    };

    const handleContactSupport = () => {
        console.log('Contact support clicked');
    };

    const handleDownloadInvoice = (invoiceUrl) => {
        window.open(invoiceUrl, "_blank");
    };


    if (loading) {
        return (
            <div className="w-full max-w-4xl mx-auto p-6">
                <div className="text-center text-gray-400 py-12">Loading permit details...</div>
            </div>
        );
    }

    if (!permitData) {
        return (
            <div className="w-full max-w-4xl mx-auto p-6">
                <div className="text-center text-gray-400 py-12">No permit data available</div>
            </div>
        );
    }

    const currentStep = getCurrentStep(permitData.permit_status, permitData.base_fees_paid, permitData.county_fees_paid, permitData.county_fees_payment_link);
    const progress = getProgress(permitData.permit_status, permitData.base_fees_paid, permitData.county_fees_paid, permitData.county_fees_payment_link);
    const permitType = getPermitTypeName(permitData.permit_type);

    return (
        <>
            <div className='my-4 text-lg text-white flex items-center gap-2 cursor-pointer' onClick={() => navigate(`/citypermit/project/chat/${permitChatId}`, { state: { permitId: permitId } })}>
                <ArrowLeft className="w-4 h-4" />
                chat
            </div>
            <div className="w-full max-w-5xl mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6">

                {/* Header Card */}
                <Card className="dark rounded-[20px] bg-[#282828] shadow-lg shadow-black/30 border-[hsl(220,14%,20%)] p-4 sm:p-8">
                    {/* Header */}
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-4 sm:mb-6">
                        <div className="flex items-center gap-3">
                            <Circle size={24} className="text-[#FEBA08] fill-[#FEBA08]" />
                            <div>
                                <h1 className="text-xl sm:text-2xl font-semibold text-white">
                                    {permitType} Permit
                                </h1>
                                <p className="text-sm text-gray-400 mt-1">
                                    {permitData.project_name} · {permitData.portal_address}
                                </p>
                            </div>
                        </div>
                        <Badge
                            variant="outline"
                            className="bg-[#FEBA08]/10 text-[#FEBA08] border-[#FEBA08]/30 uppercase text-xs px-3 py-1 rounded-full font-semibold tracking-wide"
                        >
                            {getStatusDisplay(permitData.permit_status, permitData.base_fees_paid)}
                        </Badge>
                    </div>

                    {/* Progress Section */}
                    <div className="mb-4 sm:mb-8">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-sm text-gray-400">Progress</span>
                        </div>

                        {/* Mobile: horizontally scrollable, no scrollbar — desktop: normal */}
                        <div className="overflow-x-auto no-scrollbar sm:overflow-x-visible">
                            <div className="min-w-[460px] sm:min-w-0">

                                {/* Segmented Progress Bar */}
                                <div className="flex gap-[5px] mb-7">
                                    {Array.from({ length: 13 }).map((_, i) => {
                                        const segmentThreshold = ((i + 1) / 13) * 100;
                                        const isFilled = segmentThreshold <= progress;
                                        return (
                                            <div
                                                key={i}
                                                className={`flex-1 h-[11px] rounded-[20px] transition-all duration-500 ${isFilled ? '' : 'border border-[#FEBA08]'}`}
                                                style={{ backgroundColor: isFilled ? '#FEBA08' : '' }}
                                            />
                                        );
                                    })}
                                </div>

                                {/* Steps */}
                                <div className="flex items-start justify-between relative">
                                    <div className="absolute top-3 left-0 right-0 h-px bg-[hsl(220,14%,20%)]" style={{ zIndex: 0 }} />

                                    {steps.map((step, index) => {
                                        const isCompleted = index < currentStep;
                                        const isCurrent = index === currentStep;
                                        const isActive = isCompleted || isCurrent;

                                        return (
                                            <div
                                                key={step.label}
                                                className="flex flex-col items-center relative z-10"
                                                style={{ flex: 1 }}
                                            >
                                                <div
                                                    className={[
                                                        'w-6 h-6 rounded-full flex items-center justify-center mb-2 transition-all duration-300',
                                                        isCompleted ? 'bg-[#FEBA08]' : '',
                                                        isCurrent ? 'bg-[#FEBA08] ring-[3px] ring-[#FEBA08]/25 ring-offset-1 ring-offset-[#282828]' : '',
                                                        !isActive ? 'bg-[hsl(220,14%,25%)]' : '',
                                                    ].join(' ')}
                                                >
                                                    {isCompleted && (
                                                        <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    )}
                                                </div>
                                                <span
                                                    className="text-[10px] text-center leading-tight mt-0.5 whitespace-nowrap"
                                                    style={{ color: isActive ? '#FEBA08' : 'hsl(220,14%,45%)' }}
                                                >
                                                    {step.label}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>

                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3">
                        {!permitData?.base_fees_paid ? (
                            <Button
                                onClick={() => handlePayment(permitData._id, permitData.user_id, permitData.permit_chat_id)}
                                className="flex-1 bg-[#282828] border border-black hover:bg-black/20 text-white font-semibold py-6 text-base rounded-[20px]"
                            >
                                Pay {Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(permitData.base_fees_amount || 0)}
                            </Button>
                        ) : !permitData?.county_fees_paid && permitData?.county_fees_payment_link ? (
                            <Button
                                onClick={() => window.location.href = permitData.county_fees_payment_link}
                                className="flex-1 bg-[#282828] border border-black hover:bg-black/20 text-white font-semibold py-6 text-base rounded-[20px]"
                            >
                                Pay County Fees {Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(permitData.county_fees_amount || 0)}
                            </Button>
                        ) : permitData?.base_fees_invoice_url && permitData?.county_fees_invoice_url ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button className="flex-1 bg-[#282828] border-black border hover:bg-[#e5a807] text-black font-semibold py-6 text-base">
                                        Download Invoice <ChevronDown className="ml-2 h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)]">
                                    <DropdownMenuItem
                                        onClick={() => handleDownloadInvoice(permitData.base_fees_invoice_url)}
                                        className="text-gray-300 hover:bg-[hsl(220,14%,25%)] cursor-pointer"
                                    >
                                        Base Fees Invoice
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onClick={() => handleDownloadInvoice(permitData.county_fees_invoice_url)}
                                        className="text-gray-300 hover:bg-[hsl(220,14%,25%)] cursor-pointer"
                                    >
                                        County Fees Invoice
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : permitData?.base_fees_invoice_url ? (
                            <Button
                                onClick={() => handleDownloadInvoice(permitData.base_fees_invoice_url)}
                                className="flex-1 bg-[#282828] border border-black hover:bg-black/20 text-white font-semibold py-6 text-base rounded-[20px]"
                            >
                                Download Invoice
                            </Button>
                        ) : null}
                        <Button
                            onClick={handleViewDocuments}
                            variant="outline"
                            className="flex-1 bg-black border border-black hover:bg-black/80 text-white font-semibold py-6 text-base rounded-[20px]"
                        >
                            <Lock className="w-4 h-4" />
                            County Login Details
                        </Button>
                    </div>
                </Card>

                {/* Permit Details Card */}
                <Card className="dark rounded-[20px] bg-[#282828] shadow-lg shadow-black/30 border-[hsl(220,14%,20%)] p-4 sm:p-8">
                    <h2 className="text-xl font-semibold text-white mb-4 sm:mb-6">Permit Details</h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 sm:gap-x-16 gap-y-4 sm:gap-y-6">
                        {/* Left Column */}
                        <div className="space-y-6">
                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Permit Type</p>
                                <p className="text-white font-medium">{permitType}</p>
                            </div>

                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Permit Base Fee</p>
                                <p className="text-white font-medium">${permitData.base_fees_amount || 0}</p>
                            </div>

                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Project</p>
                                <p className="text-white font-medium">{permitData.project_name}</p>
                            </div>

                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Permit ID</p>
                                <p className="text-white font-medium">{permitData._id.slice(-8)}</p>
                            </div>
                        </div>

                        {/* Right Column */}
                        <div className="space-y-6">
                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Status</p>
                                <p className="text-white font-medium">{getStatusDisplay(permitData.permit_status, permitData.base_fees_paid)}</p>
                            </div>

                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Address</p>
                                <p className="text-white font-medium">{permitData.portal_address}</p>
                            </div>

                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Created</p>
                                <p className="text-white font-medium">{formatDate(permitData.createdAt)}</p>
                            </div>

                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Portal</p>
                                <p className="text-white font-medium uppercase">{permitData.portal_name}</p>
                            </div>
                        </div>
                    </div>
                </Card>

                {/* View Documents Dialog */}
                <Dialog open={showDocDialog} onOpenChange={setShowDocDialog}>
                    <DialogContent className=
                        "dark  border-[hsl(220,14%,25%)] text-white max-w-lg rounded-[20px] backdrop-blur-md">
                        <DialogHeader>
                            <DialogTitle className="text-white">County Login Details</DialogTitle>
                        </DialogHeader>

                        {credsLoading ? (
                            <div className="flex flex-col items-center justify-center py-8 gap-3">
                                <Loader2 className="w-6 h-6 text-[#FEBA08] animate-spin" />
                                <p className="text-sm text-gray-400">Fetching credentials...</p>
                            </div>
                        ) : credsError ? (
                            <div className="py-4">
                                <p className="text-sm text-gray-300 text-center">
                                    Permit application is in process.
                                </p>
                            </div>
                        ) : portalCreds ? (
                            <div className="space-y-4">
                                <p className="text-sm text-gray-400 leading-relaxed">
                                    Please login in county website with below credentials for further procedure.
                                </p>

                                <div className="flex flex-col gap-3 my-2">
                                    {/* Portal Email */}
                                    <div className="flex items-start gap-3 p-4 rounded-xl bg-[hsl(220,14%,11%)] border border-[hsl(220,14%,22%)]">
                                        <Mail className="h-4 w-4 mt-0.5 text-[#FEBA08] shrink-0" />
                                        <div className="min-w-0">
                                            <p className="text-xs text-gray-500 mb-1">Portal Email</p>
                                            <p className="text-sm font-medium text-white break-all">
                                                {portalCreds.portal_mail || 'N/A'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Portal Password */}
                                    <div className="flex items-start gap-3 p-4 rounded-xl bg-[hsl(220,14%,11%)] border border-[hsl(220,14%,22%)]">
                                        <Key className="h-4 w-4 mt-0.5 text-[#FEBA08] shrink-0" />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-xs text-gray-500 mb-1">Portal Password</p>
                                            <p className="text-sm font-medium text-white font-mono tracking-wide">
                                                {showPassword ? (portalCreds.portal_password || 'N/A') : '••••••••••••'}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(prev => !prev)}
                                            className="text-gray-500 hover:text-gray-300 transition-colors shrink-0 mt-0.5"
                                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                                        >
                                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </DialogContent>
                </Dialog>
            </div>
        </>
    );
};

export default PermitProjectDetail;